/**
 * What the device's log means, as pure functions.
 *
 * The screen holds no derived state of its own. Score, fouls, who is on the
 * floor and what the clock reads are all recomputed from the log, which is why
 * killing the app loses nothing: the log is the truth and everything else is a
 * view of it.
 *
 * `pointsOf` and `countsAsPlayerFoul` are imported from
 * `@good-season/stats-basketball` rather than reimplemented. That package is
 * the single definition of what scores, so the number on the scoreboard and the
 * number in the box score have nowhere to disagree.
 */

import {
  BasketballEventType,
  countsAsPlayerFoul,
  countsTowardTeamFouls,
  pointsOf,
} from '@good-season/stats-basketball';
import type { CoreEvent } from '@good-season/stats-core';
import type { EventBody, GameSetup, LoggedEvent, RosterPlayer } from './db.js';

/** The marker a correction carries. ADR-0044: a void row is bare and names its target. */
export const VOID_TYPE = 'VOID';

export interface EventDraft {
  readonly type: string;
  readonly team: 'us' | 'them';
  readonly playerId?: string | null;
  readonly value?: number | null;
  readonly voidsEventId?: string | null;
  /**
   * For the two events that DEFINE a reading rather than observe one.
   *
   * A PERIOD_START sets the new period's clock to full, and it is the only
   * caller allowed to say so: everything else takes the reading the clock
   * already shows, including a play that happened after the whistle. A
   * PERIOD_START that names neither is the next period, resolved from the
   * reading inside the write (`nextPeriod`), never from a screen.
   */
  readonly period?: number;
  readonly gameClockMs?: number;
  /**
   * Client bookkeeping the row carries beside the tap id `playBody` adds:
   * Undo names the row a restored play stands in for (`restores`), and an
   * attribution names the row its copy replaced (`replaces`).
   */
  readonly meta?: Record<string, unknown>;
}

/**
 * The tap a live row belongs to, which `voidGroup`, `isOpeningRow` and
 * `undoDrafts` group on: the id in `meta.tap`, and for a row written before
 * the id existed, its wall clock, which was the identity then.
 *
 * Live rows only. A VOID row carries no `meta` at all (`playBody`), so nothing
 * may group on a VOID's key.
 */
export function tapKey(row: LoggedEvent): string {
  const tap = row.event.meta?.['tap'];
  return typeof tap === 'string' ? tap : row.event.wallClock;
}

/**
 * A PERIOD_START that names no period, as End period sends it: the period
 * after the reading, at the length that period runs. Resolved inside the write
 * from the reading taken off disk (ADR-0110), so a window whose screen missed
 * another window's period change cannot start the same period twice.
 */
export function nextPeriod(draft: EventDraft, reading: ClockState, setup: GameSetup): EventDraft {
  if (draft.type !== BasketballEventType.PERIOD_START || draft.period !== undefined) return draft;
  const period = reading.period + 1;
  const gameClockMs =
    period > setup.regulationPeriods ? setup.overtimeLengthMs : setup.regulationLengthMs;
  return { ...draft, period, gameClockMs };
}

/**
 * The row a tap records, at the reading the clock shows unless the draft
 * defines one.
 *
 * Pure, so the shape this client writes can be run through the server's
 * `GameEventInputSchema` in a test without a store underneath it. `db.ts` runs
 * that same schema at queue time, and a refusal there is a tap that fails, so
 * the shapes below are checked before they are ever tapped.
 *
 * A VOID carries no `meta`, not even the tap id. The server's append guard
 * (`void_carries_a_play` in `apps/api/src/db/repositories/scorebook.ts`)
 * refuses a voiding row that carries anything, meta included, because a NOTE's
 * whole content is its meta and a void that also records loses a play. The
 * local door does not check that, so the first version of the tap id went on
 * every row, every Undo was a 422 the outbox held forever, and nothing from
 * that game reached the server again. Found by the delta pass.
 */
export function playBody(
  draft: EventDraft,
  reading: ClockState,
  wallClock: string,
  tap: string,
): EventBody {
  return {
    type: draft.type,
    team: draft.team,
    period: draft.period ?? reading.period,
    // Rounded, never clamped. The column is an integer and the reading is
    // allowed to be negative past the end of a period.
    gameClockMs: Math.round(draft.gameClockMs ?? reading.remainingMs),
    wallClock,
    playerId: draft.playerId ?? null,
    value: draft.value ?? null,
    voidsEventId: draft.voidsEventId ?? null,
    ...(draft.type === VOID_TYPE ? {} : { meta: { ...draft.meta, tap } }),
  };
}

/**
 * Giving an unattributed play to a player: the same play again with a name on
 * it, and a VOID of the original.
 *
 * Never an edit. The queued body is immutable, and the server refuses a row
 * that both voids and records anyway, so a correction is two rows.
 *
 * The replacement carries the ORIGINAL's period and clock reading, not the
 * reading at the moment of the correction. Attributing at halftime with a
 * halftime reading moves the basket to halftime, which credits it to whoever
 * was on the floor by then and reads the shooter for zero.
 *
 * The replacement names the row it replaced (`meta.replaces`), which is how
 * Undo knows a correction from a play and what it records again (ADR-0059).
 * The VOID carries nothing, see `playBody`.
 */
export function attributionBodies(
  target: LoggedEvent,
  playerId: string,
  wallClock: string,
  tap: string,
): readonly [EventBody, EventBody] {
  return correctionBodies(target, { playerId }, wallClock, tap);
}

/**
 * Changing what kind of foul a recorded foul was: the same play again under
 * the right type, and a VOID of the original.
 *
 * The same shape as an attribution and for the same reason (never an edit), so
 * Undo reverses it the way it reverses one: the copy is voided and the original
 * recorded again (`undoDrafts`, `meta.replaces`). The copy keeps the original's
 * player, period and clock reading.
 */
export function retypeBodies(
  target: LoggedEvent,
  type: string,
  wallClock: string,
  tap: string,
): readonly [EventBody, EventBody] {
  return correctionBodies(target, { type }, wallClock, tap);
}

function correctionBodies(
  target: LoggedEvent,
  change: { readonly playerId?: string; readonly type?: string },
  wallClock: string,
  tap: string,
): readonly [EventBody, EventBody] {
  const at = { period: target.event.period, gameClockMs: target.event.gameClockMs, wallClock };
  return [
    {
      ...at,
      type: change.type ?? target.event.type,
      team: target.event.team,
      playerId: change.playerId ?? target.event.playerId ?? null,
      value: target.event.value ?? null,
      voidsEventId: null,
      meta: { tap, replaces: target.id },
    },
    {
      ...at,
      type: VOID_TYPE,
      team: target.event.team,
      playerId: null,
      value: null,
      voidsEventId: target.id,
    },
  ];
}

/**
 * A swap, as two rows at the SAME clock reading.
 *
 * The shared reading is what makes minutes and plus-minus computable
 * (BUILD_PLAN 5.5). Recording them at two readings a few hundred milliseconds
 * apart invents a lineup that was never on the floor.
 */
export function substitutionBodies(
  outPlayerId: string,
  inPlayerId: string,
  reading: ClockState,
  wallClock: string,
  tap: string,
): readonly [EventBody, EventBody] {
  const shared = {
    period: reading.period,
    gameClockMs: Math.round(reading.remainingMs),
    wallClock,
    meta: { tap },
    team: 'us' as const,
    value: null,
    voidsEventId: null,
  };
  return [
    { ...shared, type: BasketballEventType.SUB_OUT, playerId: outPlayerId },
    { ...shared, type: BasketballEventType.SUB_IN, playerId: inPlayerId },
  ];
}

/**
 * A clock reading as a person reads it, sign and all.
 *
 * The sign is carried by a leading minus and the digits stay readable, so a
 * period that has run past its end reads as -0:07 rather than as nonsense.
 * Shared by the live header and the plays timeline, which must not disagree
 * about what a reading says.
 */
export function clockLabel(remainingMs: number): string {
  const negative = remainingMs < 0;
  const total = Math.ceil(Math.abs(remainingMs) / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${negative ? '-' : ''}${String(minutes)}:${String(seconds).padStart(2, '0')}`;
}

/**
 * A typed final score, or null when what was typed is not one.
 *
 * `GameFinishSchema` bounds each side to a whole number from 0 to 999, and a
 * body outside it is a 422 the coach can do nothing with. Checked here so the
 * screen can say which number to change instead.
 *
 * The empty string is refused explicitly, because `Number('')` is 0 and a coach
 * who clears the field would otherwise close the book at nil.
 */
export function finalScoreFrom(entered: string): number | null {
  const trimmed = entered.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isInteger(value) && value >= 0 && value <= 999 ? value : null;
}

export interface ClockState {
  readonly period: number;
  readonly running: boolean;
  /**
   * Milliseconds remaining, and NEGATIVE once a period has run past its
   * nominal end.
   *
   * **Never clamped at zero, and this is the highest-consequence line in the
   * file.** A running-clock league, a slow whistle or an overrun all produce
   * readings past the end. Clamping makes a substitution and a basket that both
   * land at a clamped 0 indistinguishable: `groupByClockReading` merges them and
   * credits the points to the lineup that just left the floor, so the player who
   * came on and scored reads zero minutes and zero plus-minus. The engine is
   * doing the only thing it can with what it was given, so no server fix
   * recovers it. The information has to survive here or not at all.
   */
  readonly remainingMs: number;
}

const CLOCK_ANCHORS: ReadonlySet<string> = new Set([
  BasketballEventType.CLOCK_START,
  BasketballEventType.CLOCK_STOP,
  BasketballEventType.PERIOD_START,
  BasketballEventType.PERIOD_END,
  BasketballEventType.SET_CLOCK,
]);

/**
 * Events that name a player when one is known, so a missing player is a real
 * gap rather than the normal shape of the event.
 *
 * A team rebound, a timeout and a period marker have no player by definition
 * and must never be counted as unattributed, or the badge would show a number
 * the scorekeeper cannot ever clear.
 */
const ATTRIBUTABLE: ReadonlySet<string> = new Set([
  BasketballEventType.FG2_MADE,
  BasketballEventType.FG2_MISS,
  BasketballEventType.FG3_MADE,
  BasketballEventType.FG3_MISS,
  BasketballEventType.FT_MADE,
  BasketballEventType.FT_MISS,
  BasketballEventType.REB_OFF,
  BasketballEventType.REB_DEF,
  BasketballEventType.AST,
  BasketballEventType.STL,
  BasketballEventType.BLK,
  BasketballEventType.TO,
  BasketballEventType.DEFLECTION,
  BasketballEventType.CHARGE_TAKEN,
  BasketballEventType.FOUL_PERSONAL,
  BasketballEventType.FOUL_SHOOTING,
  BasketballEventType.FOUL_OFFENSIVE,
  BasketballEventType.FOUL_TECHNICAL,
  BasketballEventType.FOUL_FLAGRANT,
]);

/**
 * What a recorded type is called in front of a parent.
 *
 * The copy lives here rather than in the sport package, which holds no display
 * strings by design and would have to hold a second set for every sport in
 * every language. Every type this client can write has an entry; a type from a
 * newer client falls back rather than printing the wire spelling, because a
 * registry identifier is not a sentence anybody should be shown.
 */
const PLAY_LABEL: Readonly<Record<string, string>> = {
  [BasketballEventType.FG2_MADE]: 'Two made',
  [BasketballEventType.FG2_MISS]: 'Two missed',
  [BasketballEventType.FG3_MADE]: 'Three made',
  [BasketballEventType.FG3_MISS]: 'Three missed',
  [BasketballEventType.FT_MADE]: 'Free throw made',
  [BasketballEventType.FT_MISS]: 'Free throw missed',
  [BasketballEventType.REB_OFF]: 'Offensive rebound',
  [BasketballEventType.REB_DEF]: 'Defensive rebound',
  [BasketballEventType.REB_TEAM]: 'Team rebound',
  [BasketballEventType.AST]: 'Assist',
  [BasketballEventType.STL]: 'Steal',
  [BasketballEventType.BLK]: 'Block',
  [BasketballEventType.TO]: 'Turnover',
  [BasketballEventType.DEFLECTION]: 'Deflection',
  [BasketballEventType.CHARGE_TAKEN]: 'Charge taken',
  [BasketballEventType.FOUL_PERSONAL]: 'Foul',
  [BasketballEventType.FOUL_SHOOTING]: 'Shooting foul',
  [BasketballEventType.FOUL_OFFENSIVE]: 'Offensive foul',
  [BasketballEventType.FOUL_TECHNICAL]: 'Technical foul',
  [BasketballEventType.FOUL_FLAGRANT]: 'Flagrant foul',
  [BasketballEventType.SUB_IN]: 'Came on',
  [BasketballEventType.SUB_OUT]: 'Came off',
  [BasketballEventType.PERIOD_START]: 'Period started',
  [BasketballEventType.PERIOD_END]: 'Period ended',
  [BasketballEventType.CLOCK_START]: 'Clock started',
  [BasketballEventType.CLOCK_STOP]: 'Clock stopped',
  [BasketballEventType.SET_CLOCK]: 'Clock corrected',
  [BasketballEventType.TIMEOUT_FULL]: 'Timeout',
  [BasketballEventType.TIMEOUT_SHORT]: 'Short timeout',
  [BasketballEventType.JUMP_BALL]: 'Jump ball',
  [BasketballEventType.NOTE]: 'Note',
};

/**
 * The foul kinds a scorekeeper can change a foul to, in the order they are
 * offered. Personal is the default every foul is recorded as, so it is the
 * kind a change goes BACK to, not one of the four reached for.
 */
export const FOUL_KINDS: readonly { readonly type: string; readonly label: string }[] = [
  { type: BasketballEventType.FOUL_PERSONAL, label: 'Personal' },
  { type: BasketballEventType.FOUL_SHOOTING, label: 'Shooting' },
  { type: BasketballEventType.FOUL_OFFENSIVE, label: 'Offensive' },
  { type: BasketballEventType.FOUL_TECHNICAL, label: 'Technical' },
  { type: BasketballEventType.FOUL_FLAGRANT, label: 'Flagrant' },
];

export function describePlay(row: LoggedEvent): string {
  return PLAY_LABEL[row.event.type] ?? 'Play';
}

/** The most a note may hold. See `noteFrom`. */
export const NOTE_MAX_CHARS = 160;

/** The key a NOTE keeps its words under, in `meta`. */
export const NOTE_META_KEY = 'text';

/**
 * A typed note, judged.
 *
 * The cap is in characters and sits well under the server's 1024 byte `meta`
 * limit even at four bytes a character with the tap id beside it, so a note
 * that passes here is never one the outbox would hold forever. A note over it
 * is refused rather than cut, because a note silently shortened is a note that
 * says something the coach did not write.
 */
export function noteFrom(
  entered: string,
): { readonly kind: 'ok'; readonly text: string } | { readonly kind: 'empty' | 'long' } {
  const text = entered.trim();
  if (text === '') return { kind: 'empty' };
  return Array.from(text).length > NOTE_MAX_CHARS ? { kind: 'long' } : { kind: 'ok', text };
}

/** The words a NOTE row carries, or an empty string for any other row. */
export function noteTextOf(row: LoggedEvent): string {
  if (row.event.type !== BasketballEventType.NOTE) return '';
  const text = row.event.meta?.[NOTE_META_KEY];
  return typeof text === 'string' ? text : '';
}

export interface TimeoutsUsed {
  readonly full: number;
  readonly short: number;
}

/**
 * Timeouts each side has taken in one period (a half in a two period game).
 * Counted from the live log, so a timeout taken back stops counting.
 */
export function timeoutsIn(
  log: readonly LoggedEvent[],
  period: number,
): { readonly us: TimeoutsUsed; readonly them: TimeoutsUsed } {
  const used = { us: { full: 0, short: 0 }, them: { full: 0, short: 0 } };
  for (const row of liveEvents(log)) {
    if (row.event.period !== period) continue;
    const side = used[row.event.team];
    if (row.event.type === BasketballEventType.TIMEOUT_FULL) side.full += 1;
    if (row.event.type === BasketballEventType.TIMEOUT_SHORT) side.short += 1;
  }
  return used;
}

/** What a player is called on this device. A preferred name wins if there is one. */
export function nameOf(player: RosterPlayer): string {
  return player.preferredName ?? player.firstName;
}

/**
 * Whether a play should name a player and does not (BUILD_PLAN 5.7).
 *
 * Our team only, and only the types that name one by definition, which is the
 * same rule `tally` counts the badge with. An opponent play carries no player
 * on purpose and a period marker never had one, so neither is a gap a
 * scorekeeper can ever close.
 */
export function isUnattributed(row: LoggedEvent): boolean {
  return namesAPlayer(row) && typeof row.event.playerId !== 'string';
}

/** Whether a play is the kind that credits one of our players, named or not yet. */
export function namesAPlayer(row: LoggedEvent): boolean {
  return row.event.team === 'us' && ATTRIBUTABLE.has(row.event.type);
}

/**
 * The box score's count of the same gap the live book's badge counts
 * (BUILD_PLAN 5.7), for a plain `CoreEvent` log rather than this device's own
 * `LoggedEvent` rows: `BoxScoreScreen.tsx` reads events this shape from both
 * its local cache and the server, post void-resolution, so there is no `VOID`
 * row left to filter the way `isUnattributed` does for the live book.
 */
export function unattributedCount(events: readonly CoreEvent[]): number {
  return events.filter(
    (event) => event.team === 'us' && ATTRIBUTABLE.has(event.type) && event.playerId === null,
  ).length;
}

/** The ids this device has voided. The only holder of them, per ADR-0045. */
export function voidedIds(log: readonly LoggedEvent[]): ReadonlySet<string> {
  const voided = new Set<string>();
  for (const row of log) {
    const target = row.event.voidsEventId;
    if (row.event.type === VOID_TYPE && typeof target === 'string') voided.add(target);
  }
  return voided;
}

/** The plays that still count: neither a void row nor voided by one. */
export function liveEvents(log: readonly LoggedEvent[]): readonly LoggedEvent[] {
  const voided = voidedIds(log);
  return log.filter((row) => row.event.type !== VOID_TYPE && !voided.has(row.id));
}

/**
 * Whether a live row is one Start wrote: the first period's opening and the
 * starters' `SUB_IN`s, one tap (`openingBodies`).
 *
 * Nobody tapped these as plays, so the timeline does not list them and Undo
 * does not reach them. Identified by the period-one `PERIOD_START` beside
 * them, which only Start writes. An earlier version keyed on "a `SUB_IN` with
 * no `SUB_OUT`", which is also what a player filling an open spot writes, and
 * that one IS a tap a scorekeeper may take back.
 */
export function isOpeningRow(row: LoggedEvent, live: readonly LoggedEvent[]): boolean {
  const tap = tapKey(row);
  return live.some(
    (other) =>
      tapKey(other) === tap &&
      other.event.type === BasketballEventType.PERIOD_START &&
      other.event.period === 1,
  );
}

/**
 * Whether a live row is one a scorekeeper may take back on its own.
 *
 * Two kinds are not. A `PERIOD_START` is half of a period change and is taken
 * back through the `PERIOD_END` it travels with, never alone: voiding only the
 * start leaves the old period marked ended with plays still landing in it. And
 * the opening lineup: voiding one starter leaves four on the floor with no
 * swap that can ever make five again, because every swap takes one off for one
 * on.
 */
export function isVoidable(row: LoggedEvent, live: readonly LoggedEvent[]): boolean {
  return row.event.type !== BasketballEventType.PERIOD_START && !isOpeningRow(row, live);
}

/**
 * Every live row the same tap wrote as `target`: the whole swap, the whole
 * period change, or the one play.
 *
 * Taking back half a swap is what this exists to prevent: a `SUB_IN` voided
 * alone leaves the `SUB_OUT` standing and the floor one short for the rest of
 * the game.
 */
export function voidGroup(
  live: readonly LoggedEvent[],
  target: LoggedEvent,
): readonly LoggedEvent[] {
  const tap = tapKey(target);
  return live.filter((row) => tapKey(row) === tap);
}

/** The correction for one row: a bare VOID naming it (ADR-0044). */
export function voidDraft(row: LoggedEvent): EventDraft {
  return { type: VOID_TYPE, team: row.event.team, voidsEventId: row.id };
}

/**
 * The row a restored play stands in for: the one whose tap it is ordered by,
 * and whose `replaces` names what to record again when it is reversed. A
 * restored row names the ROOT of its chain, so a restore of a restore still
 * points at the row the correction wrote.
 */
function restoredFrom(row: LoggedEvent): string {
  const restores = row.event.meta?.['restores'];
  return typeof restores === 'string' ? restores : row.id;
}

/**
 * The row a correction's copy replaced, or undefined for a plain play.
 *
 * Read from the copy's `meta.replaces`. A copy written before the name
 * existed carries no tap id either, and its VOID shares its wall clock, which
 * was the tap identity then (ADR-0058): for that row the VOID is found by the
 * clock, as it was before. The first version of the name dropped that case
 * while `tapKey` kept its own fallback, so Undo of an older correction voided
 * the copy bare and the basket left the board. Found by the delta pass.
 */
function replacedBy(
  copy: LoggedEvent,
  log: readonly LoggedEvent[],
  byId: ReadonlyMap<string, LoggedEvent>,
): LoggedEvent | undefined {
  const replaces = copy.event.meta?.['replaces'];
  if (typeof replaces === 'string') return byId.get(replaces);
  if (copy.event.meta?.['tap'] !== undefined) return undefined;
  const voided = log.find(
    (entry) => entry.event.type === VOID_TYPE && entry.event.wallClock === copy.event.wallClock,
  )?.event.voidsEventId;
  return typeof voided === 'string' ? byId.get(voided) : undefined;
}

/**
 * The same play again, as it was recorded, naming the row it stands in for.
 *
 * The name is what keeps Undo walking. `undoDrafts` orders a restored row by
 * its ROOT's tap and reads what to record again from the root's `replaces`,
 * never from the restored row's own meta: read as a row of its own, the
 * restored play was the newest tap and stood ahead of a later correction on
 * a different play, and after "give it to Y" then "move it to Z" the second
 * Undo voided Y bare instead of putting X back. Found by both passes.
 */
function restoreDraft(row: LoggedEvent): EventDraft {
  const { type, team, playerId, value, period, gameClockMs } = row.event;
  return {
    type,
    team,
    playerId: playerId ?? null,
    value: value ?? null,
    period,
    gameClockMs,
    meta: { restores: restoredFrom(row) },
  };
}

/**
 * What Undo writes: the newest tap that may be taken back, reversed. Empty
 * when there is nothing left.
 *
 * A play's tap is reversed by voiding every row it wrote. A CORRECTION's tap
 * (an attribution: a named copy plus a VOID of the unnamed original, the copy
 * naming the original in `meta.replaces`) is reversed by voiding the copy AND
 * recording the original again as it was, because un-voiding is not a thing
 * (ADR-0045). Without that second half, Undo after "give it to #12" removed
 * the basket from the score altogether, which is not what the button says it
 * does. Found by the fresh-context pass.
 *
 * The recorded-again play stands for the tap it restores, so the press after
 * that takes IT back, which is the original play undone: the walk goes
 * correction, play, previous play, and so on back to the opening lineup, which
 * stays. It cannot reach a correction made on ANOTHER device, because no read
 * returns a voiding row.
 */
export function undoDrafts(log: readonly LoggedEvent[]): readonly EventDraft[] {
  const live = liveEvents(log);
  const byId = new Map(log.map((entry) => [entry.id, entry]));
  // The tap a live row stands for: its own, or for a restored play the tap
  // of the row it restores.
  const rootOf = (row: LoggedEvent): LoggedEvent => byId.get(restoredFrom(row)) ?? row;
  /*
   * Newest TAP first, not newest row. A restored play is always the newest
   * ROW (a fresh clientSeq) while standing for an older tap, and walking by
   * row picked it ahead of a later correction on a DIFFERENT play: after
   * correcting two plays and undoing the second correction, the next press
   * voided the restored play bare instead of reversing the first correction,
   * and that play's basket left the board. Found by the delta pass.
   *
   * Ordered by the ROOT's clientSeq, which `db.ts` persists, and not by its
   * wall clock, which one round used: a device clock stepped back across a
   * restart gave a new tap an earlier stamp than an older one, and Undo
   * reversed the wrong play. A sequence does not go back (it wraps at two
   * billion events, which no device reaches).
   */
  const newestTapFirst = [...live].sort(
    (left, right) =>
      rootOf(right).event.clientSeq - rootOf(left).event.clientSeq ||
      right.event.clientSeq - left.event.clientSeq,
  );
  for (const row of newestTapFirst) {
    if (!isVoidable(row, live)) continue;

    const drafts = voidGroup(live, row).map(voidDraft);
    // What the row replaced, if it is a correction's copy: read from the
    // row itself, unless the row is a restored play, in which case from the
    // row it stands in for.
    const original = replacedBy(rootOf(row), log, byId);
    if (original !== undefined) drafts.push(restoreDraft(original));
    return drafts;
  }
  return [];
}

/**
 * The game's first rows: the period opening at full, and one `SUB_IN` per
 * starter at that same reading.
 *
 * The engine's lineup starts EMPTY and only a `SUB_IN` ever adds to it
 * (`computeStints` in `packages/stats-core`), so a starting five kept only in
 * this device's cache is a game where nobody is credited a minute until the
 * first substitution, and the first `SUB_OUT` is a warning about a player who
 * was never on the floor. The log is permanent and the server is where minutes
 * are computed, so the starters have to be IN it.
 */
export function openingBodies(
  starters: readonly string[],
  regulationLengthMs: number,
  wallClock: string,
  tap: string,
): readonly EventBody[] {
  const at = {
    period: 1,
    gameClockMs: regulationLengthMs,
    wallClock,
    meta: { tap },
    team: 'us' as const,
  };
  return [
    {
      ...at,
      type: BasketballEventType.PERIOD_START,
      playerId: null,
      value: null,
      voidsEventId: null,
    },
    ...starters.map((playerId) => ({
      ...at,
      type: BasketballEventType.SUB_IN,
      playerId,
      value: null,
      voidsEventId: null,
    })),
  ];
}

/**
 * The clock as it reads right now.
 *
 * Derived from the log plus wall time rather than held in a ticking variable,
 * so it survives a refresh, a phone lock and a crash (BUILD_PLAN 5.6). The
 * caller supplies `nowMs`: this module has no ambient time, which is what makes
 * it testable at an exact reading.
 */
export function clockAt(log: readonly LoggedEvent[], nowMs: number, setup: GameSetup): ClockState {
  const live = liveEvents(log);

  let period = 1;
  let anchor: LoggedEvent | null = null;
  let running = false;
  for (const row of live) {
    const type = row.event.type;
    if (type === BasketballEventType.PERIOD_START) period = row.event.period;
    if (!CLOCK_ANCHORS.has(type)) continue;
    anchor = row;
    // A correction moves the reading and leaves the clock running or stopped
    // as it was (BUILD_PLAN 5.6). Reading every non-start anchor as a stop,
    // which this did, froze the display on a correction typed mid-play.
    if (type === BasketballEventType.CLOCK_START) running = true;
    else if (type !== BasketballEventType.SET_CLOCK) running = false;
  }

  if (anchor === null) {
    return { period, running: false, remainingMs: setup.regulationLengthMs };
  }

  if (!running) {
    return { period, running: false, remainingMs: anchor.event.gameClockMs };
  }

  // Never negative: a tap stamped before a start another window wrote after
  // it reads that start's own value, not one counted backwards above where the
  // clock began. Clamped rather than dropping later-stamped rows from the
  // reading, which a device clock stepped back would turn into a lost period.
  const since = Math.max(0, nowMs - Date.parse(anchor.event.wallClock));
  // No clamp on the result. See `ClockState.remainingMs`.
  return { period, running: true, remainingMs: anchor.event.gameClockMs - since };
}

/**
 * A reading as the scoreboard shows it, in milliseconds remaining, or null
 * when what was typed is not one. "7:11", "0:07.5", "45" (seconds alone).
 * Never negative: a scoreboard does not show one.
 */
export function parseClock(entered: string): number | null {
  const match = /^\s*(?:(\d{1,2}):)?(\d{1,2})(?:\.(\d))?\s*$/.exec(entered);
  if (match === null) return null;
  const minutes = Number(match[1] ?? 0);
  const seconds = Number(match[2]);
  if (match[1] !== undefined && seconds >= 60) return null;
  return (minutes * 60 + seconds) * 1000 + Number(match[3] ?? 0) * 100;
}

/**
 * Team fouls in one period, each side, counted the way the box score counts
 * them (`countsTowardTeamFouls`, per period, `boxscore.ts`). The bonus
 * indicator is one of the few things a scorekeeper announces out loud, so it
 * reads the same rule the engine does rather than a copy.
 */
export function teamFoulsIn(
  log: readonly LoggedEvent[],
  period: number,
): { readonly us: number; readonly them: number } {
  let us = 0;
  let them = 0;
  for (const row of liveEvents(log)) {
    if (row.event.period !== period || !countsTowardTeamFouls(row.event.type)) continue;
    if (row.event.team === 'us') us += 1;
    else them += 1;
  }
  return { us, them };
}

/**
 * Where a player stands against the disqualifying count: fine, one away, or
 * out. The box score's comparison (`pf >= disqualifyingFouls`), on the same
 * count (`countsAsPlayerFoul`, which is what `tally` counts), for every value
 * the client can produce (4, 5 or 6). The zero guard is defensive only: zero
 * is a soccer config's value, which no basketball book ever opens, and the
 * box score itself would mark everybody out at zero.
 */
export function foulStanding(fouls: number, disqualifyingFouls: number): 'ok' | 'trouble' | 'out' {
  if (disqualifyingFouls <= 0) return 'ok';
  if (fouls >= disqualifyingFouls) return 'out';
  return fouls === disqualifyingFouls - 1 ? 'trouble' : 'ok';
}

/** Whether a side's team fouls this period have put the OTHER side in the bonus. */
export function bonusStanding(
  teamFouls: number,
  teamFoulsForBonus: number,
  teamFoulsForDoubleBonus: number,
): 'none' | 'bonus' | 'double' {
  if (teamFoulsForDoubleBonus > 0 && teamFouls >= teamFoulsForDoubleBonus) return 'double';
  if (teamFoulsForBonus > 0 && teamFouls >= teamFoulsForBonus) return 'bonus';
  return 'none';
}

/**
 * Who is on the floor: the starting five, then every substitution applied in
 * order.
 *
 * Derived rather than stored for the same reason as the clock. A device that
 * reopens mid-third-quarter has to show the right five without having kept any
 * state in memory.
 */
export function onCourt(log: readonly LoggedEvent[], setup: GameSetup): readonly string[] {
  const floor = new Set(setup.startingPlayerIds);
  for (const row of liveEvents(log)) {
    const playerId = row.event.playerId;
    if (typeof playerId !== 'string') continue;
    if (row.event.type === BasketballEventType.SUB_IN) floor.add(playerId);
    if (row.event.type === BasketballEventType.SUB_OUT) floor.delete(playerId);
  }
  return [...floor];
}

export interface PlayerTally {
  readonly points: number;
  readonly fouls: number;
}

export interface Tally {
  readonly ourScore: number;
  readonly theirScore: number;
  readonly byPlayer: ReadonlyMap<string, PlayerTally>;
  /** Plays that should name a player and do not, for the badge (BUILD_PLAN 5.7). */
  readonly unattributed: number;
}

/**
 * The live scoreboard.
 *
 * Not a box score, and deliberately not trying to be one: it is the
 * scorekeeper's feedback loop, so it counts points and personal fouls and
 * nothing else. The derived box score is the sport engine's job and needs the
 * game config, the full log from every device, and the stint model.
 */
export function tally(log: readonly LoggedEvent[]): Tally {
  let ourScore = 0;
  let theirScore = 0;
  let unattributed = 0;
  const byPlayer = new Map<string, PlayerTally>();

  for (const row of liveEvents(log)) {
    const event = row.event;
    const points = pointsOf(toCoreEvent(row));

    if (event.team === 'us') ourScore += points;
    else theirScore += points;

    // The badge count and the timeline's flag come from ONE predicate. Two
    // copies of this rule would let the badge show a number the timeline has
    // no row for, which is a count a scorekeeper cannot ever clear.
    if (isUnattributed(row)) unattributed += 1;

    const playerId = event.playerId;
    if (typeof playerId !== 'string') continue;

    const current = byPlayer.get(playerId) ?? { points: 0, fouls: 0 };
    byPlayer.set(playerId, {
      points: current.points + points,
      fouls: current.fouls + (countsAsPlayerFoul(event.type) ? 1 : 0),
    });
  }

  return { ourScore, theirScore, byPlayer, unattributed };
}

/**
 * A logged event in the shape the engine reads.
 *
 * `voidedAt` is null throughout: this device's voids are expressed as VOID rows
 * and `liveEvents` has already removed what they name, so nothing reaching the
 * engine through here is voided. The server's own read sets it from a column.
 */
export function toCoreEvent(row: LoggedEvent): CoreEvent {
  const event = row.event;
  return {
    id: event.id,
    clientSeq: event.clientSeq,
    deviceId: event.deviceId,
    period: event.period,
    gameClockMs: event.gameClockMs,
    wallClock: event.wallClock,
    type: event.type,
    team: event.team,
    playerId: event.playerId ?? null,
    value: event.value ?? null,
    meta: event.meta ?? {},
    voidedAt: null,
  };
}
